import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, FileText, Eye, Code, FileDown } from 'lucide-react';

const ExamPaperGenerator = () => {
  const [generatedPaper, setGeneratedPaper] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('formatted'); // 'formatted' or 'latex'
  const [mathJaxLoaded, setMathJaxLoaded] = useState(false);

  // Load MathJax
  useEffect(() => {
    const loadMathJax = () => {
      if (window.MathJax) {
        setMathJaxLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://polyfill.io/v3/polyfill.min.js?features=es6';
      script.onload = () => {
        const mathJaxScript = document.createElement('script');
        mathJaxScript.id = 'MathJax-script';
        mathJaxScript.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
        mathJaxScript.async = true;
        mathJaxScript.onload = () => {
          window.MathJax = {
            tex: {
              inlineMath: [['$', '$'], ['\\(', '\\)']],
              displayMath: [['$$', '$$'], ['\\[', '\\]']],
              processEscapes: true,
              processEnvironments: true
            },
            options: {
              ignoreHtmlClass: 'tex2jax_ignore',
              processHtmlClass: 'tex2jax_process'
            },
            startup: {
              pageReady: () => {
                setMathJaxLoaded(true);
              }
            }
          };
        };
        document.head.appendChild(mathJaxScript);
      };
      document.head.appendChild(script);
    };

    loadMathJax();
  }, []);

  // Re-render MathJax when content changes
  useEffect(() => {
    if (mathJaxLoaded && generatedPaper && viewMode === 'formatted') {
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise();
      }
    }
  }, [generatedPaper, viewMode, mathJaxLoaded]);

  // Enhanced question templates with marks allocation
  const questionTemplates = {
    sectionA: [
      // Simple arithmetic - can be paired
      () => ({
        question: `Add: $431 + 69$`,
        marks: 2,
        canPair: true,
        type: 'arithmetic'
      }),
      () => ({
        question: `Simplify: $8 - 4$`,
        marks: 2,
        canPair: true,
        type: 'arithmetic'
      }),
      () => {
        const a = Math.floor(Math.random() * 400) + 200;
        const b = Math.floor(Math.random() * 80) + 20;
        return {
          question: `Add: $${a} + ${b}$`,
          marks: 2,
          canPair: true,
          type: 'arithmetic'
        };
      },
      () => {
        const a = Math.floor(Math.random() * 10) + 5;
        const b = Math.floor(Math.random() * a);
        return {
          question: `Simplify: $${a} - ${b}$`,
          marks: 2,
          canPair: true,
          type: 'arithmetic'
        };
      },
      // Geometry and angles - needs more space
      () => ({
        question: `What is the size of the smaller angle between North East and South East?`,
        marks: 3,
        canPair: false,
        type: 'geometry'
      }),
      () => {
        const numbers = [247, 389, 512, 634, 758, 891];
        const num = numbers[Math.floor(Math.random() * numbers.length)];
        return {
          question: `Write ${num} in Roman numerals.`,
          marks: 2,
          canPair: true,
          type: 'conversion'
        };
      },
      // Fractions - can be paired
      () => ({
        question: `Work out: $\\frac{5}{6} - \\frac{2}{3}$`,
        marks: 3,
        canPair: true,
        type: 'fraction'
      }),
      () => {
        const fractions = [
          ["\\frac{7}{8}", "\\frac{1}{4}"],
          ["\\frac{3}{5}", "\\frac{1}{10}"],
          ["\\frac{4}{7}", "\\frac{2}{7}"],
          ["\\frac{5}{9}", "\\frac{1}{3}"]
        ];
        const [f1, f2] = fractions[Math.floor(Math.random() * fractions.length)];
        return {
          question: `Work out: $${f1} - ${f2}$`,
          marks: 3,
          canPair: true,
          type: 'fraction'
        };
      },
      // Variable substitution - needs space
      () => {
        const x = (Math.random() * 5 + 2).toFixed(1);
        const y = (Math.random() * 8 + 3).toFixed(1);
        const z = (Math.random() * 3 + 1).toFixed(1);
        return {
          question: `Given that $X = ${x}$, $y = ${y}$ and $Z = ${z}$, find the value of $X + y - Z$.`,
          marks: 3,
          canPair: false,
          type: 'algebra'
        };
      },
      // Sequences - needs space
      () => ({
        question: `Find the sum of the missing numbers in the sequence below:\n$1, 4, 7, 10, \\_, \\_$`,
        marks: 4,
        canPair: false,
        type: 'sequence'
      }),
      // Statistics - can be paired if simple
      () => {
        const nums = Array.from({length: 4}, () => Math.floor(Math.random() * 10) + 1);
        return {
          question: `Find the mean of the numbers below:\n$${nums.join(', ')}$`,
          marks: 3,
          canPair: true,
          type: 'statistics'
        };
      },
      // Triangle angles - needs diagram space
      () => ({
        question: `Calculate the size of angle X in the figure given below.\n\n\\textit{Description of figure: A triangle with angles marked as $60°$, $X°$, and $X°$.}`,
        marks: 4,
        canPair: false,
        type: 'geometry'
      }),
      // Set operations
      () => {
        const sets = [
          ["\\{2, 3, 4, 5, 6\\}", "\\{2, 6, 8, 10\\}"],
          ["\\{1, 3, 5, 7, 9\\}", "\\{3, 7, 11, 13\\}"],
          ["\\{0, 2, 4, 6, 8\\}", "\\{0, 4, 12, 16\\}"]
        ];
        const [setP, setQ] = sets[Math.floor(Math.random() * sets.length)];
        return {
          question: `Let Set $P = ${setP}$ and Set $Q = ${setQ}$. Find $n(P - Q)$`,
          marks: 3,
          canPair: false,
          type: 'sets'
        };
      },
      // More simple questions that can be paired
      () => {
        const price = Math.floor(Math.random() * 100) + 150;
        const total = price * (Math.floor(Math.random() * 60) + 40);
        return {
          question: `How many ${price} shilling pancakes can be bought with ${total} shillings?`,
          marks: 2,
          canPair: true,
          type: 'arithmetic'
        };
      },
      () => {
        const numbers = Array.from({length: 7}, () => Math.floor(Math.random() * 15) + 1).sort((a,b) => a-b);
        return {
          question: `Write down the median of the numbers:\n$${numbers.join(', ')}$`,
          marks: 2,
          canPair: true,
          type: 'statistics'
        };
      },
      () => {
        const percentage = Math.floor(Math.random() * 20) + 50;
        const count = Math.floor(Math.random() * 20) + 15;
        return {
          question: `${percentage}% of the pupils in a class are girls. If there are ${count} girls in the class, how many pupils are in that class?`,
          marks: 3,
          canPair: false,
          type: 'percentage'
        };
      }
    ],
    
    sectionB: [
      () => ({
        question: `In a club of 35 pupils, X like only Games $n(G)$, 22 like Music $n(M)$, 12 like both activities and 4 pupils do not like either.

\\textit{Description of Venn diagram: Two overlapping circles labeled $n(M)$ and $n(G)$ with intersection labeled $n(\\xi)$}

(a) Show the above information on the Venn diagram below. \\hfill (5 marks)

(b) Find the value of X. \\hfill (2 marks)

(c) How many pupils like two types of activities? \\hfill (2 marks)

(d) If a child is chosen at random to be the captain of the club, what is the probability of choosing a child who likes only music? \\hfill (2 marks)`,
        marks: 11,
        parts: ['a', 'b', 'c', 'd']
      }),
      
      () => {
        const petrol = (Math.random() * 5 + 5).toFixed(2);
        const diesel = (Math.random() * 20 + 50).toFixed(1);
        const fraction = Math.floor(Math.random() * 3) + 2;
        const whole = Math.floor(Math.random() * 3) + 3;
        
        return {
          question: `(a) A person bought ${petrol} litres of petrol for his car and ${diesel} litres of diesel for his truck. Find how much fuel he bought. \\hfill (2 marks)

(b) What number of $\\frac{${fraction}}{4}$ litres of petrol are contained in $${whole}\\frac{1}{2}$ litres? \\hfill (2 marks)`,
          marks: 4,
          parts: ['a', 'b']
        };
      },
      
      () => {
        const startHour = Math.floor(Math.random() * 3) + 8;
        const startMin = Math.floor(Math.random() * 60);
        const endHour = startHour + Math.floor(Math.random() * 3) + 2;
        const endMin = Math.floor(Math.random() * 60);
        const speed = Math.floor(Math.random() * 40) + 60;
        const timeHours = Math.floor(Math.random() * 2) + 2;
        const timeFraction = Math.floor(Math.random() * 3) + 1;
        
        return {
          question: `(a) A youth counselling session begun at ${startHour}:${startMin.toString().padStart(2, '0')}a.m. and ended at ${endHour}:${endMin.toString().padStart(2, '0')}a.m. Find the length of time the session lasted. \\hfill (2 marks)

(b) The bus left Kampala for Mbarara while moving at a speed of ${speed}Km/hr. If the bus took $${timeHours}\\frac{${timeFraction}}{4}$ hours on the way. What is the distance between Kampala and Mbarara? \\hfill (3 marks)`,
          marks: 5,
          parts: ['a', 'b']
        };
      }
    ]
  };

  const generateExamPaper = () => {
    setIsGenerating(true);
    
    setTimeout(() => {
      const sectionAQuestions = [];
      const sectionBQuestions = [];
      
      // Generate Section A with pairing logic
      let questionNumber = 1;
      let availableQuestions = [...questionTemplates.sectionA];
      
      while (questionNumber <= 20 && availableQuestions.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableQuestions.length);
        const generator = availableQuestions[randomIndex];
        const question1 = generator();
        
        if (question1.canPair && questionNumber < 20) {
          // Try to find another pairable question of similar type
          const pairableQuestions = availableQuestions.filter((q, i) => {
            const testQ = q();
            return testQ.canPair && testQ.type === question1.type && i !== randomIndex;
          });
          
          if (pairableQuestions.length > 0) {
            const question2 = pairableQuestions[0]();
            sectionAQuestions.push({
              number: questionNumber,
              question: question1,
              paired: true,
              pairNumber: questionNumber + 1,
              pairQuestion: question2
            });
            questionNumber += 2;
          } else {
            sectionAQuestions.push({
              number: questionNumber,
              question: question1,
              paired: false
            });
            questionNumber += 1;
          }
        } else {
          sectionAQuestions.push({
            number: questionNumber,
            question: question1,
            paired: false
          });
          questionNumber += 1;
        }
        
        // Remove used generator to avoid repetition
        availableQuestions.splice(randomIndex, 1);
        if (availableQuestions.length === 0) {
          availableQuestions = [...questionTemplates.sectionA];
        }
      }
      
      // Generate Section B
      for (let i = 0; i < 12; i++) {
        const generator = questionTemplates.sectionB[i % questionTemplates.sectionB.length];
        sectionBQuestions.push({
          number: i + 21,
          question: generator()
        });
      }
      
      setGeneratedPaper({
        sectionA: sectionAQuestions,
        sectionB: sectionBQuestions,
        timestamp: new Date().toLocaleString()
      });
      
      setIsGenerating(false);
    }, 1500);
  };

  const generateLatexContent = () => {
    if (!generatedPaper) return '';
    
    let latex = `\\documentclass[12pt,a4paper]{article}
\\usepackage[margin=2cm]{geometry}
\\usepackage{amsmath}
\\usepackage{amssymb}
\\usepackage{enumitem}
\\usepackage{tikz}
\\usepackage{pgfplots}

\\title{Mathematics Examination Paper}
\\date{Generated on: ${generatedPaper.timestamp}}
\\author{}

\\begin{document}
\\maketitle

\\section*{Instructions}
\\begin{itemize}
\\item Answer all questions
\\item Show all working clearly
\\item Write your answers in the spaces provided
\\end{itemize}

\\section*{SECTION A}
\\textit{Answer all questions in this section}

`;

    generatedPaper.sectionA.forEach((q) => {
      if (q.paired) {
        latex += `\\begin{minipage}[t]{0.48\\textwidth}
\\textbf{${q.number}.} ${q.question.question} \\hfill [${q.question.marks} marks]

\\vspace{3cm}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.48\\textwidth}
\\textbf{${q.pairNumber}.} ${q.pairQuestion.question} \\hfill [${q.pairQuestion.marks} marks]

\\vspace{3cm}
\\end{minipage}

\\vspace{1cm}

`;
      } else {
        latex += `\\textbf{${q.number}.} ${q.question.question} \\hfill [${q.question.marks} marks]

\\vspace{${q.question.type === 'geometry' || q.question.type === 'sequence' ? '4cm' : '3cm'}}

`;
      }
    });

    latex += `\\newpage
\\section*{SECTION B}
\\textit{Answer all questions in this section}

`;

    generatedPaper.sectionB.forEach((q) => {
      latex += `\\textbf{${q.number}.} ${q.question.question}

\\vspace{6cm}

`;
    });

    latex += `\\end{document}`;
    return latex;
  };

  const generateHtmlContent = () => {
    if (!generatedPaper) return '';
    
    let html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Mathematics Examination Paper</title>
    <script src="https://polyfill.io/v3/polyfill.min.js?features=es6"></script>
    <script id="MathJax-script" async src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"></script>
    <script>
        window.MathJax = {
            tex: {
                inlineMath: [['$', '$'], ['\\\\(', '\\\\)']],
                displayMath: [['$$', '$$'], ['\\\\[', '\\\\]']]
            }
        };
    </script>
    <style>
        body { 
            font-family: 'Times New Roman', serif; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px; 
            line-height: 1.6;
        }
        .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
        }
        .section { 
            margin: 30px 0; 
        }
        .question { 
            margin: 20px 0; 
            padding: 15px 0;
        }
        .question-pair {
            display: flex;
            justify-content: space-between;
            gap: 20px;
        }
        .question-pair > div {
            flex: 1;
        }
        .marks { 
            float: right; 
            font-weight: bold;
        }
        .space { 
            height: 80px; 
            border-bottom: 1px solid #ddd;
            margin: 10px 0;
        }
        .large-space { 
            height: 120px; 
            border-bottom: 1px solid #ddd;
            margin: 10px 0;
        }
        .instructions {
            background-color: #f5f5f5;
            padding: 15px;
            border-left: 4px solid #333;
            margin-bottom: 30px;
        }
        @media print {
            body { margin: 0; }
            .space { height: 100px; }
            .large-space { height: 150px; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>MATHEMATICS EXAMINATION PAPER</h1>
        <p>Generated on: ${generatedPaper.timestamp}</p>
    </div>
    
    <div class="instructions">
        <h3>Instructions:</h3>
        <ul>
            <li>Answer all questions</li>
            <li>Show all working clearly</li>
            <li>Write your answers in the spaces provided</li>
        </ul>
    </div>

    <div class="section">
        <h2>SECTION A</h2>
        <p><em>Answer all questions in this section</em></p>
`;

    generatedPaper.sectionA.forEach((q) => {
      if (q.paired) {
        html += `
        <div class="question question-pair">
            <div>
                <strong>${q.number}.</strong> ${q.question.question} 
                <span class="marks">[${q.question.marks} marks]</span>
                <div class="space"></div>
            </div>
            <div>
                <strong>${q.pairNumber}.</strong> ${q.pairQuestion.question} 
                <span class="marks">[${q.pairQuestion.marks} marks]</span>
                <div class="space"></div>
            </div>
        </div>`;
      } else {
        const spaceClass = (q.question.type === 'geometry' || q.question.type === 'sequence') ? 'large-space' : 'space';
        html += `
        <div class="question">
            <strong>${q.number}.</strong> ${q.question.question} 
            <span class="marks">[${q.question.marks} marks]</span>
            <div class="${spaceClass}"></div>
        </div>`;
      }
    });

    html += `
    </div>

    <div class="section">
        <h2>SECTION B</h2>
        <p><em>Answer all questions in this section</em></p>
`;

    generatedPaper.sectionB.forEach((q) => {
      html += `
        <div class="question">
            <strong>${q.number}.</strong> ${q.question.question}
            <div class="large-space"></div>
            <div class="large-space"></div>
        </div>`;
    });

    html += `
    </div>
</body>
</html>`;
    
    return html;
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    // Note: This is a simplified PDF generation using HTML to PDF conversion
    // In a real application, you might want to use a proper PDF library like jsPDF or PDFKit
    const htmlContent = generateHtmlContent();
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for MathJax to render, then print
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 1000);
    }, 2000);
  };

  const renderMathJax = (text) => {
    // Process LaTeX expressions for MathJax rendering
    return text
      .replace(/\\\\/g, '') // Remove double backslashes
      .replace(/\\textit\{([^}]+)\}/g, '<em>$1</em>') // Convert \textit to <em>
      .replace(/\\hfill/g, '') // Remove \hfill
      .replace(/\\_/g, '_') // Convert \_ to _
      .replace(/\\\*/g, '*') // Convert \* to *
      .replace(/\\\[/g, '\\[') // Ensure proper display math
      .replace(/\\\]/g, '\\]') // Ensure proper display math
      .replace(/\\\(/g, '\\(') // Ensure proper inline math
      .replace(/\\\)/g, '\\)'); // Ensure proper inline math
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-800">
                Enhanced Examination Paper Generator
              </h1>
            </div>
            
            <button
              onClick={generateExamPaper}
              disabled={isGenerating}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${isGenerating ? 'animate-spin' : ''}`} />
              <span>{isGenerating ? 'Generating...' : 'Generate New Paper'}</span>
            </button>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <p className="text-blue-800">
              Enhanced examination paper generator with proper formatting, spacing for answers, 
              mark allocation, and multiple download formats including LaTeX, HTML, and PDF.
            </p>
          </div>
        </div>

        {generatedPaper && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-800">Generated Examination Paper</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setViewMode('formatted')}
                    className={`flex items-center space-x-1 px-3 py-1 rounded ${
                      viewMode === 'formatted' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    <span>Formatted</span>
                  </button>
                  <button
                    onClick={() => setViewMode('latex')}
                    className={`flex items-center space-x-1 px-3 py-1 rounded ${
                      viewMode === 'latex' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    <span>LaTeX</span>
                  </button>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => downloadFile(generateHtmlContent(), `exam-paper-${Date.now()}.html`, 'text/html')}
                  className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>HTML</span>
                </button>
                <button
                  onClick={downloadPDF}
                  className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  <span>PDF</span>
                </button>
                <button
                  onClick={() => downloadFile(generateLatexContent(), `exam-paper-${Date.now()}.tex`, 'text/plain')}
                  className="flex items-center space-x-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Code className="w-4 h-4" />
                  <span>LaTeX</span>
                </button>
                <button
                  onClick={() => {
                    const plainText = generateLatexContent().replace(/\\[a-zA-Z]+\{?/g, '').replace(/[{}]/g, '');
                    downloadFile(plainText, `exam-paper-${Date.now()}.txt`, 'text/plain');
                  }}
                  className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Text</span>
                </button>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mb-6">
              Generated on: {generatedPaper.timestamp}
            </div>

            {viewMode === 'formatted' ? (
              <div className="space-y-8 font-serif">
                <div className="text-center border-b-2 border-gray-300 pb-4 mb-8">
                  <h1 className="text-2xl font-bold mb-2">MATHEMATICS EXAMINATION PAPER</h1>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h3 className="font-bold mb-2">Instructions:</h3>
                    <ul className="text-left list-disc list-inside space-y-1">
                      <li>Answer all questions</li>
                      <li>Show all working clearly</li>
                      <li>Write your answers in the spaces provided</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-2 border-gray-200 pb-2">
                    SECTION A
                  </h3>
                  <p className="text-sm italic mb-6">Answer all questions in this section</p>
                  
                  <div className="space-y-6">
                    {generatedPaper.sectionA.map((q) => (
                      <div key={q.number} className={q.paired ? "grid grid-cols-2 gap-8" : ""}>
                        <div className="border border-gray-200 p-4 rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <div className="text-gray-800 whitespace-pre-line flex-1 tex2jax_process" dangerouslySetInnerHTML={{
                              __html: `<span class="font-bold">${q.number}.</span> ${renderMathJax(q.question.question)}`
                            }} />
                            <span className="text-sm font-bold text-blue-600 ml-4">[{q.question.marks} marks]</span>
                          </div>
                          <div className={`border-b border-gray-300 ${
                            q.question.type === 'geometry' || q.question.type === 'sequence' ? 'h-24' : 'h-16'
                          }`}></div>
                        </div>
                        
                        {q.paired && (
                          <div className="border border-gray-200 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-3">
                              <div className="text-gray-800 whitespace-pre-line flex-1 tex2jax_process" dangerouslySetInnerHTML={{
                                __html: `<span class="font-bold">${q.pairNumber}.</span> ${renderMathJax(q.pairQuestion.question)}`
                              }} />
                              <span className="text-sm font-bold text-blue-600 ml-4">[{q.pairQuestion.marks} marks]</span>
                            </div>
                            <div className="border-b border-gray-300 h-16"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-2 border-gray-200 pb-2">
                    SECTION B
                  </h3>
                  <p className="text-sm italic mb-6">Answer all questions in this section</p>
                  
                  <div className="space-y-8">
                    {generatedPaper.sectionB.map((q) => (
                      <div key={q.number} className="border border-gray-200 p-6 rounded-lg">
                        <div className="mb-4">
                          <div className="text-gray-800 whitespace-pre-line tex2jax_process" dangerouslySetInnerHTML={{
                            __html: `<span class="font-bold">${q.number}.</span> ${renderMathJax(q.question.question)}`
                          }} />
                        </div>
                        <div className="space-y-4">
                          <div className="border-b border-gray-300 h-20"></div>
                          <div className="border-b border-gray-300 h-20"></div>
                          <div className="border-b border-gray-300 h-20"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-auto max-h-96">
                <pre className="whitespace-pre-wrap">{generateLatexContent()}</pre>
              </div>
            )}
          </div>
        )}

        {!generatedPaper && !isGenerating && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No Paper Generated</h3>
            <p className="text-gray-500">Click "Generate New Paper" to create your first examination paper with proper formatting.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamPaperGenerator;